import create, {
  GetState,
  PartialState,
  SetState,
  State,
  StoreApi,
} from 'zustand';

// Internal state for revertable states
type revertableState = {
  addState: (s: State) => void;
  getLastState: () => State;
  states: Array<State>;
};

export const useRevertableState = create<revertableState>((set, get) => ({
  states: [],
  addState: (newS: State) => {
    const states = get().states;
    if (states.length > 10) {
      states.shift();
    }
    states.push(newS);
    set((state) => ({
      ...state,
      states,
    }));
  },
  getLastState: () => {
    const states = get().states;
    const lastState = states.pop();
    set((state) => ({ ...state, states: states }));
    return lastState as State;
  },
}));

// revertable middleware function
export const revertable = <S extends State>(
  fn: (set: SetState<S>, get: GetState<S>, api: StoreApi<S>) => S
) => (
  set: SetState<S> & { revert?: () => void },
  get: GetState<S>,
  api: StoreApi<S> & { revert?: () => void }
): S => {
  api.revert = () => {
    const lastState = useRevertableState.getState().getLastState();
    if (!lastState) {
      return;
    }
    // @ts-ignore
    lastState.reverted = true;
    set(lastState as PartialState<S>);
  };
  const res = fn(
    (...args) => {
      const state = get();
      useRevertableState.getState().addState(state);
      (state as S & { reverted: boolean }).reverted = false;
      set(...args);
    },
    get,
    api
  );
  return { ...res, reverted: false };
};
